import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';

import {
  RequirementsApi,
  type Comment,
  type Framework,
  type Log,
  type Requirement,
  type RequirementsApiBulkUpdateRequirementsControlsRequest,
  type RequirementsApiCreateRequirementCommentsRequest,
  type RequirementsApiGetRequirementLogsRequest,
  type RequirementsApiGetRequirementMappedFrameworksRequest,
  type RequirementsApiGetRequirementRequest,
  type RequirementsApiGetRequirementsCommentsRequest,
  type RequirementsApiGetRequirementsControlsRequest,
  type RequirementsApiUpdateRequirementOwnershipRequest,
  type RequirementsApiUpdateRequirementStatusRequest,
  type ControlPaginated,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import { AxiosError } from 'axios';

export const useRequirementStorev2 = defineStore('requirement-v2', () => {
  const currentRequirement = ref<Requirement | null>(null);
  const requirementControls = ref<ControlPaginated>({
    total: 0,
    items: [],
    page: 0,
    size: 0,
    pages: 0,
  });
  const requirementComments = ref<Comment[]>([]);
  const requirementLogs = ref<Log[]>([]);
  const requirementMappedFrameworks = ref<Framework[]>([]);

  const getRequirement = async (params: RequirementsApiGetRequirementRequest) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.getRequirement(params);

      if (!response) return;

      currentRequirement.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateRequirementStatus = async (params: RequirementsApiUpdateRequirementStatusRequest) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.updateRequirementStatus(params);

      if (!response) return;

      currentRequirement.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateRequirementOwner = async (
    params: RequirementsApiUpdateRequirementOwnershipRequest,
  ) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.updateRequirementOwnership(params);

      if (!response) return;

      currentRequirement.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRequirementControls = async (params: RequirementsApiGetRequirementsControlsRequest) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.getRequirementsControls(params);

      if (!response) return;

      requirementControls.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateRequirementControlsBulk = async (
    params: RequirementsApiBulkUpdateRequirementsControlsRequest,
  ) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.bulkUpdateRequirementsControls(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRequirementComments = async (params: RequirementsApiGetRequirementsCommentsRequest) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.getRequirementsComments(params);

      if (!response) return;

      requirementComments.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createRequirementComments = async (
    params: RequirementsApiCreateRequirementCommentsRequest,
  ) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.createRequirementComments(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRequirementLogs = async (params: RequirementsApiGetRequirementLogsRequest) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.getRequirementLogs(params);

      if (!response) return;

      requirementLogs.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRequirementMappedFrameworks = async (
    params: RequirementsApiGetRequirementMappedFrameworksRequest,
  ) => {
    try {
      const requirementsApi = await initApi(RequirementsApi);
      const response = await requirementsApi?.getRequirementMappedFrameworks(params);

      if (!response) return;

      requirementMappedFrameworks.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetStore = () => {
    currentRequirement.value = null;
    requirementControls.value = {
      total: 0,
      items: [],
      page: 0,
      size: 0,
      pages: 0,
    };
    requirementComments.value = [];
    requirementLogs.value = [];
    requirementMappedFrameworks.value = [];
  };

  return {
    getRequirement,
    updateRequirementStatus,
    updateRequirementOwner,
    getRequirementControls,
    updateRequirementControlsBulk,
    getRequirementComments,
    getRequirementLogs,
    createRequirementComments,
    getRequirementMappedFrameworks,
    resetStore,

    currentRequirement,
    requirementControls,
    requirementComments,
    requirementLogs,
    requirementMappedFrameworks,
  };
});
